import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Divider,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  Chip,
  Link,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  OutlinedInput,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DashboardHistoryTabs from './DashboardHistoryTabs'
import {
  DashboardPage,
  DashboardSignOff,
  DashboardSignOffRevoked,
} from '../../types/gotypes'

import {
  DEFAULT_SHARE_ACCESS_LEVEL,
  ShareAccessFields,
  ShareLinkField,
  sharePasswordMissing,
} from './ShareControls'

export { PUBLIC_SHARE_WARNING } from './ShareControls'

// 'appearance' is gone (#1182): brand colours moved to the per-page tab panel
// (PageBrandColorsPanel), and the dashboard name lives on About.
export type DashboardSettingsTabKey = 'sharing' | 'about' | 'history'

export interface DashboardSettingsSaveUpdates {
  name: string
  description: string
  objectives: string
  keyMetrics: string
  pages: DashboardPage[]
}

interface DashboardSettingsModalProps {
  open: boolean
  initialTab?: DashboardSettingsTabKey
  name: string
  description: string
  objectives: string
  keyMetrics?: string
  pages: DashboardPage[]
  onSave: (updates: DashboardSettingsSaveUpdates) => void
  onClose: () => void
  isLoading?: boolean
  // Sharing / history props
  customer?: string
  job?: string
  dashboardId?: string
  shareId?: string
  shareAccessLevel?: string
  shareEnabled?: boolean
  onShare?: (accessLevel: string, password?: string) => Promise<void>
  onDisableShare?: () => Promise<void>
  shareIsLoading?: boolean
  // History tab sign-off context (#974) — the History tab shows the same two
  // sub-tabs as the preview-area History panel, so it needs the same sign-off
  // state the owning page already holds. Optional: without it the Sign-offs
  // sub-tab still lists recorded events, it just cannot state current state.
  currentSignOff?: DashboardSignOff
  signOffRevoked?: DashboardSignOffRevoked
  widgetTitles?: Record<string, string>
  // About tab client/job context (#973 item 7) — display names, per-level
  // Context descriptions, and click-through navigation, all supplied by the
  // owning page (page → hook → component).
  customerDisplayName?: string
  jobDisplayName?: string
  customerContext?: string
  jobContext?: string
  onOpenCustomer?: () => void
  onOpenJob?: () => void
  // "Save as template" (#1179): the row action moved off the DataViz homepage
  // into here, so the capability stays reachable from inside the dashboard.
  // Optional — the owning page supplies the dialog + create call.
  onSaveAsTemplate?: () => void
  // Multi-job management (#1162). The About tab gains a Jobs section: the
  // current set, with the primary fixed, an add-job picker from the
  // customer's other jobs, and remove for a job no widget references.
  // Omitted (undefined jobs) → the section is hidden, e.g. legacy dashboards.
  jobs?: string[]
  primaryJob?: string
  availableJobs?: { name: string; displayName?: string }[]
  /** Job id → how many widgets reference it; a job in use cannot be removed. */
  referencedJobs?: Record<string, number>
  onAddJob?: (job: string) => void
  onRemoveJob?: (job: string) => void
  jobMutationLoading?: boolean
}

// One expandable Client / Job row for the About tab: display name links
// through to the entity, and the row expands to show its Context description.
const ContextEntityRow: React.FC<{
  label: string
  displayName: string
  description?: string
  onNavigate?: () => void
}> = ({ label, displayName, description, onNavigate }) => {
  const [expanded, setExpanded] = useState(false)
  const hasDescription = Boolean(description && description.trim())
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ minWidth: 48 }}>
          <strong>{label}:</strong>
        </Typography>
        {onNavigate ? (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={onNavigate}
            sx={{ textAlign: 'left' }}
          >
            {displayName}
          </Link>
        ) : (
          <Typography variant="body2">{displayName}</Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {hasDescription && (
          <IconButton
            size="small"
            aria-label={expanded ? `Hide ${label.toLowerCase()} description` : `Show ${label.toLowerCase()} description`}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>
      {hasDescription && (
        <Collapse in={expanded} unmountOnExit>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ pl: 7, pt: 0.5, whiteSpace: 'pre-wrap' }}
          >
            {description}
          </Typography>
        </Collapse>
      )}
    </Box>
  )
}

// Question label placed ABOVE its box (#973 second comment): plain questions
// instead of terse field labels.
const QuestionField: React.FC<{
  question: string
  ariaLabel: string
  value: string
  onChange: (value: string) => void
  rows?: number
}> = ({ question, ariaLabel, value, onChange, rows = 3 }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
      {question}
    </Typography>
    <TextField
      fullWidth
      value={value}
      onChange={(e) => onChange(e.target.value)}
      multiline
      rows={rows}
      slotProps={{ htmlInput: { 'aria-label': ariaLabel } }}
    />
  </Box>
)

const DashboardSettingsModal: React.FC<DashboardSettingsModalProps> = ({
  open,
  initialTab = 'about',
  name: initialName,
  description: initialDescription,
  objectives: initialObjectives,
  keyMetrics: initialKeyMetrics = '',
  pages: initialPages,
  onSave,
  onClose,
  isLoading,
  customer,
  job,
  dashboardId,
  shareId,
  shareAccessLevel,
  shareEnabled,
  onShare,
  onDisableShare,
  shareIsLoading,
  currentSignOff,
  signOffRevoked,
  widgetTitles,
  customerDisplayName,
  jobDisplayName,
  customerContext,
  jobContext,
  onOpenCustomer,
  onOpenJob,
  onSaveAsTemplate,
  jobs,
  primaryJob,
  availableJobs,
  referencedJobs,
  onAddJob,
  onRemoveJob,
  jobMutationLoading,
}) => {
  const [addJobValue, setAddJobValue] = useState('')
  const [name, setName] = useState(initialName)
  const [description, setDescription] = useState(initialDescription)
  const [objectives, setObjectives] = useState(initialObjectives)
  const [keyMetrics, setKeyMetrics] = useState(initialKeyMetrics)
  const [pages, setPages] = useState<DashboardPage[]>(initialPages)
  // Which page's context accordion is expanded (#973 Option A). Keyed on the
  // page's stable ID, never its index or name, so reordering or renaming a
  // page cannot move the expansion to a different page's box.
  const [expandedPageId, setExpandedPageId] = useState('')
  const [accessLevel, setAccessLevel] = useState<string>(shareAccessLevel || DEFAULT_SHARE_ACCESS_LEVEL)
  const hasPassword = Boolean(shareEnabled && shareAccessLevel === 'password')
  const [password, setPassword] = useState('')
  const [embedCopied, setEmbedCopied] = useState(false)

  const showSharing = Boolean(dashboardId && dashboardId !== 'new' && onShare)
  // History is fetched per customer+dashboard — without a customer the request
  // can only 404, so don't offer a tab that could never load.
  const showHistory = Boolean(customer && dashboardId && dashboardId !== 'new')

  // About is deliberately FIRST and the default tab (#973): the modal opens on
  // About, so order and default must agree.
  const tabs: { key: DashboardSettingsTabKey; label: string }[] = [
    { key: 'about', label: 'About' },
    ...(showSharing ? [{ key: 'sharing' as const, label: 'Sharing' }] : []),
    ...(showHistory ? [{ key: 'history' as const, label: 'History' }] : []),
  ]
  const [activeTabIndex, setActiveTabIndex] = useState(() => {
    const idx = tabs.findIndex((t) => t.key === initialTab)
    return idx >= 0 ? idx : 0
  })
  const activeTab = tabs[activeTabIndex]?.key || 'about'

  // Reset form when the modal transitions from closed to open. The Dialog is
  // never unmounted (it just toggles `open`), and several of the props read
  // here (initialPages, initialPalette, legacyPalette) get fresh identities
  // on every parent re-render — an effect keyed on those props would re-fire
  // and stomp in-progress edits while the modal is still open (e.g. saving a
  // brand theme re-renders the parent, which re-ran this reset and reverted
  // typed Description/Objectives/page-context text). Guard on `open` alone,
  // during render, per project convention for one-shot init tied to a prop
  // transition rather than a full effect.
  const prevOpenRef = React.useRef(open)
  if (prevOpenRef.current !== open) {
    prevOpenRef.current = open
    if (open) {
      setName(initialName)
      setDescription(initialDescription)
      setObjectives(initialObjectives)
      setKeyMetrics(initialKeyMetrics)
      setPages(initialPages)
      setExpandedPageId('')
      setAccessLevel(shareAccessLevel || DEFAULT_SHARE_ACCESS_LEVEL)
      setPassword('')
      setEmbedCopied(false)

      const initialIndex = tabs.findIndex((t) => t.key === initialTab)
      setActiveTabIndex(initialIndex >= 0 ? initialIndex : 0)
    }
  }

  const handleSave = () => {
    if (!name.trim()) return
    onSave({
      name: name.trim(),
      description: description.trim(),
      objectives: objectives.trim(),
      keyMetrics: keyMetrics.trim(),
      pages,
    })
  }

  const handlePageContextChange = (pageId: string, context: string) => {
    setPages((prev) => prev.map((p) => (p.id === pageId ? { ...p, context } : p)))
  }

  const handleShare = async () => {
    if (!onShare) return
    await onShare(accessLevel, accessLevel === 'password' ? password : undefined)
    setPassword('')
  }

  const handleDisableShare = async () => {
    if (!onDisableShare) return
    await onDisableShare()
  }

  const handleCopyEmbed = () => {
    if (!shareId) return
    const url = `${window.location.origin}/dashboard/${shareId}`
    const embedCode = `<iframe src="${url}" width="100%" height="600" frameborder="0" style="border: 1px solid #e0e0e0; border-radius: 4px;"></iframe>`
    navigator.clipboard.writeText(embedCode)
    setEmbedCopied(true)
    setTimeout(() => setEmbedCopied(false), 2000)
  }

  const showPrompt = !description.trim() && !objectives.trim()

  const editHereNote = 'Inherited from the client and job — edit it in Admin → Context.'

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Dashboard Settings</DialogTitle>
      <Tabs
        value={activeTabIndex}
        onChange={(_e, v) => setActiveTabIndex(v)}
        sx={{ px: 3, borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
      >
        {tabs.map((t) => (
          <Tab key={t.key} label={t.label} sx={{ minHeight: 40, textTransform: 'none' }} />
        ))}
      </Tabs>
      <DialogContent>
        {activeTab === 'sharing' && showSharing && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <ShareAccessFields
              accessLevel={accessLevel}
              onAccessLevelChange={setAccessLevel}
              password={password}
              onPasswordChange={setPassword}
              hasPassword={hasPassword}
            />

            <Button
              variant="contained"
              color="info"
              onClick={handleShare}
              disabled={shareIsLoading || sharePasswordMissing(accessLevel, password, hasPassword)}
              startIcon={shareIsLoading ? <CircularProgress size={16} /> : undefined}
              sx={{ alignSelf: 'flex-start' }}
            >
              {shareEnabled ? 'Update Sharing' : 'Publish'}
            </Button>

            {shareEnabled && shareId && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <ShareLinkField shareId={shareId} onDisable={handleDisableShare} disabled={shareIsLoading} />
                <Divider sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Embed Code
                  </Typography>
                </Divider>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  value={`<iframe src="${window.location.origin}/dashboard/${shareId}" width="100%" height="600" frameborder="0" style="border: 1px solid #e0e0e0; border-radius: 4px;"></iframe>`}
                  slotProps={{
                    input: {
                      readOnly: true,
                      sx: { fontFamily: 'monospace', fontSize: '0.75rem' },
                    },
                  }}
                  helperText={embedCopied ? 'Copied!' : 'Paste this code into any website to embed this dashboard'}
                />
                <Button
                  size="small"
                  color="info"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyEmbed}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Copy Embed Code
                </Button>
              </Box>
            )}
          </Box>
        )}

        {activeTab === 'about' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* The name lived on the removed Appearance tab (#1182) — it now
                leads About, which is also the tab a new dashboard opens on. */}
            <TextField
              fullWidth
              label="Dashboard Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />

            {/* Inherited Client/Job context comes next (#973): it frames what
                the dashboard sits inside before we ask the author to describe it. */}
            <Divider>
              <Typography variant="caption" color="text.secondary">
                Client &amp; Job Context
              </Typography>
            </Divider>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <ContextEntityRow
                label="Client"
                displayName={customerDisplayName || customer || '—'}
                description={customerContext}
                onNavigate={onOpenCustomer}
              />
              <ContextEntityRow
                label="Job"
                displayName={jobDisplayName || job || '—'}
                description={jobContext}
                onNavigate={onOpenJob}
              />
              <Typography variant="caption" color="text.secondary">
                {editHereNote}
              </Typography>
            </Box>

            {/* Jobs (#1162): the dashboard's job set. The primary job is
                fixed; extra jobs can be added from the customer's other jobs
                and removed when no widget references them. */}
            {jobs && jobs.length > 0 && onAddJob && onRemoveJob && (
              <>
                <Divider>
                  <Typography variant="caption" color="text.secondary">Jobs</Typography>
                </Divider>
                {/* #1229 C5: shaded so the job set reads as one block, apart
                    from the fields around it. */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    This dashboard can draw tables from these jobs. A widget's
                    data always stays within the set.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {jobs.map((j) => {
                      const isPrimary = j === (primaryJob || jobs[0])
                      const refs = referencedJobs?.[j] || 0
                      const displayName = availableJobs?.find((a) => a.name === j)?.displayName || j
                      const removeReason = isPrimary
                        ? 'The primary job is fixed for this dashboard'
                        : refs > 0
                          ? `${refs} widget${refs === 1 ? '' : 's'} still reference this job`
                          : ''
                      return (
                        <Box key={j} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                            {displayName}
                            {isPrimary && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>primary</Typography>}
                          </Typography>
                          <Tooltip title={removeReason}>
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Remove job ${j}`}
                                disabled={isPrimary || refs > 0 || jobMutationLoading}
                                onClick={() => onRemoveJob(j)}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      )
                    })}
                  </Box>
                  {(() => {
                    const addable = (availableJobs || []).filter((a) => !jobs.includes(a.name))
                    if (addable.length === 0) return null
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <FormControl size="small" sx={{ flex: 1 }}>
                          <InputLabel id="add-job-label" shrink>Add a job</InputLabel>
                          <Select
                            labelId="add-job-label"
                            value={addJobValue}
                            displayEmpty
                            input={<OutlinedInput notched label="Add a job" />}
                            onChange={(e) => setAddJobValue(e.target.value)}
                          >
                            <MenuItem value=""><em>Select a job…</em></MenuItem>
                            {addable.map((a) => (
                              <MenuItem key={a.name} value={a.name}>{a.displayName || a.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          disabled={!addJobValue || jobMutationLoading}
                          onClick={() => { onAddJob(addJobValue); setAddJobValue('') }}
                        >
                          Add
                        </Button>
                      </Box>
                    )
                  })()}
                </Box>
              </>
            )}

            {showPrompt && (
              <Alert severity="info">
                Tell us what this dashboard is about so we can give better insights.
              </Alert>
            )}

            {/* Dashboard-wide context, then per-page context below it — the
                layering Matt asked for (#973): the assistant reads customer →
                job → dashboard → page, so the About tab is ordered the same
                way the model reads it. */}
            <Divider sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Dashboard-wide context
              </Typography>
            </Divider>

            <QuestionField
              question="What does this dashboard cover?"
              ariaLabel="Description"
              value={description}
              onChange={setDescription}
            />
            <QuestionField
              question="What insights are we trying to uncover and/or communicate?"
              ariaLabel="Objectives"
              value={objectives}
              onChange={setObjectives}
            />
            <QuestionField
              question="Are there any metric definitions that would improve agent insight?"
              ariaLabel="Key Metrics"
              value={keyMetrics}
              onChange={setKeyMetrics}
            />

            {/* Per-page context accordion (#973 Option A). The list is built
                from the dashboard's LIVE pages — add, remove or reorder a page
                in the builder and this follows, with no page list of its own to
                go stale. Every binding is on page.id, so renaming a page
                relabels its row without orphaning the context underneath. */}
            {pages.length > 0 && (
              <>
                <Divider sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Page context
                  </Typography>
                </Divider>
                <Typography variant="body2" color="text.secondary">
                  Describe what each page covers. Insights is per page — when someone opens
                  it on a page, this is the context it reads for that page.
                </Typography>
                <Box>
                  {pages.map((p) => (
                    <Accordion
                      key={p.id}
                      expanded={expandedPageId === p.id}
                      onChange={(_e, isExpanded) => setExpandedPageId(isExpanded ? p.id : '')}
                      disableGutters
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1 }}>
                          {p.name || 'Untitled page'}
                        </Typography>
                        {p.context?.trim() && (
                          <Chip label="Context set" size="small" variant="outlined" color="info" />
                        )}
                      </AccordionSummary>
                      <AccordionDetails>
                        <TextField
                          fullWidth
                          value={p.context || ''}
                          onChange={(e) => handlePageContextChange(p.id, e.target.value)}
                          multiline
                          rows={3}
                          placeholder="Describe what this page covers"
                          slotProps={{ htmlInput: { 'aria-label': `Context for ${p.name || 'Untitled page'}` } }}
                        />
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </>
            )}
          </Box>
        )}

        {/* #974 — same two sub-tabs as the preview-area History panel
            ("Text edits" and "Sign-offs"), from the same components. This tab
            used to show text edits only, with no sign-offs at all. */}
        {activeTab === 'history' && showHistory && (
          <DashboardHistoryTabs
            active={open && activeTab === 'history'}
            customer={customer || ''}
            dashboardId={dashboardId || ''}
            currentSignOff={currentSignOff}
            signOffRevoked={signOffRevoked}
            widgetTitles={widgetTitles}
          />
        )}
      </DialogContent>
      <DialogActions>
        {onSaveAsTemplate && activeTab !== 'history' && (
          <Button onClick={onSaveAsTemplate} disabled={isLoading} sx={{ mr: 'auto' }}>
            Save as template
          </Button>
        )}
        {activeTab === 'history' ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="info"
              disabled={!name.trim() || isLoading}
            >
              Save
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default DashboardSettingsModal
